import { isConditionInSequence, isDeviceAction, stripCafeNodeData } from '@cafe/shared';
import { findBackEdges } from '../analyzer/topology';
import { BaseStrategy } from './base';
/**
 * Native strategy for simple tree-shaped automations
 * Generates standard nested Home Assistant YAML with choose blocks
 */
export class NativeStrategy extends BaseStrategy {
    name = 'native';
    description = 'Generates nested HA YAML for simple tree-shaped automations';
    canHandle(analysis) {
        return analysis.isTree;
    }
    /** Repeat patterns detected in the current flow */
    repeatPatterns = new Map();
    /** Set of all node IDs that are internal to a repeat pattern */
    repeatInternalNodeIds = new Set();
    /** Set of back-edge IDs detected via DFS */
    backEdgeIds = new Set();
    generate(flow, analysis) {
        const warnings = [];
        // Structurally detect back-edges using DFS
        this.backEdgeIds = findBackEdges(flow);
        // Pre-detect repeat patterns from structural back-edges
        this.repeatPatterns = this.detectRepeatPatterns(flow);
        this.repeatInternalNodeIds = new Set();
        for (const pattern of this.repeatPatterns.values()) {
            for (const id of pattern.bodyNodeIds)
                this.repeatInternalNodeIds.add(id);
            for (const id of pattern.conditionNodeIds)
                this.repeatInternalNodeIds.add(id);
            if (pattern.initNodeId)
                this.repeatInternalNodeIds.add(pattern.initNodeId);
            if (pattern.incrementNodeId)
                this.repeatInternalNodeIds.add(pattern.incrementNodeId);
        }
        // Extract triggers from the flow
        const triggers = this.extractTriggers(flow);
        // Build action sequence starting from first node after triggers
        const entryNodes = analysis.entryNodes;
        const firstActions = entryNodes.flatMap((entryId) => {
            const outgoing = this.getOutgoingEdges(flow, entryId);
            return outgoing.map((e) => e.target);
        });
        // Remove duplicates
        const uniqueFirstActions = [...new Set(firstActions)];
        // Check if leading conditions can be promoted to root conditions block.
        // Conditions directly after triggers with no else/false paths can be placed
        // in the root "conditions:" block so HA properly tracks "Last triggered at".
        let rootConditions = null;
        let actionsStartNodeIds = [];
        let promotedVisited = null;
        if (uniqueFirstActions.length === 1) {
            const promoted = this.extractLeadingConditions(flow, uniqueFirstActions[0]);
            if (promoted.conditions.length > 0) {
                rootConditions = promoted.conditions;
                actionsStartNodeIds = promoted.nextNodeIds;
                promotedVisited = promoted.visitedIds;
            }
        }
        // Build the action sequence
        let actions;
        if (rootConditions) {
            // Leading conditions promoted to root - build actions from continuation point(s)
            // If there are multiple starting points (fan-out), build sequences from all of them
            if (actionsStartNodeIds.length > 0) {
                actions = actionsStartNodeIds.flatMap((nodeId) => this.buildSequenceFromNode(flow, nodeId, new Set(promotedVisited)));
            }
            else {
                actions = [];
            }
        }
        else if (uniqueFirstActions.length === 1) {
            actions = this.buildSequenceFromNode(flow, uniqueFirstActions[0], new Set());
        }
        else if (uniqueFirstActions.length > 1) {
            // Check if this is an OR pattern: all first actions are conditions
            // whose true/false paths converge to the same target
            const orPattern = this.detectOrPattern(flow, uniqueFirstActions);
            if (orPattern) {
                // Build OR condition block
                const orConditions = orPattern.conditions.map((c) => this.buildCondition(c));
                const visited = new Set(orPattern.conditions.map((c) => c.id));
                const thenSequence = this.buildSequenceFromNode(flow, orPattern.convergenceNode, visited);
                actions = [
                    {
                        if: [{ condition: 'or', conditions: orConditions }],
                        then: thenSequence,
                        else: orPattern.isFromFalsePaths ? [] : [], // OR conditions from true paths have empty else
                    },
                ];
            }
            else {
                // Check if all first-action nodes are `condition: trigger` nodes.
                // In that case, each branch is an exclusive trigger-id route and should
                // be emitted as sequential `if:` blocks, not a parallel block.
                const allAreTriggerConditions = uniqueFirstActions.every((nodeId) => {
                    const node = flow.nodes.find((n) => n.id === nodeId);
                    return (node?.type === 'condition' &&
                        node.data?.condition === 'trigger');
                });
                if (allAreTriggerConditions) {
                    // Emit each as a sequential if block
                    actions = uniqueFirstActions.flatMap((nodeId) => this.buildSequenceFromNode(flow, nodeId, new Set()));
                }
                else {
                    // Multiple paths from triggers - use parallel
                    const parallelBranches = uniqueFirstActions.map((nodeId) => this.buildSequenceFromNode(flow, nodeId, new Set()));
                    // Flatten single-action branches to avoid double-nesting (- - service:)
                    const flattenedBranches = parallelBranches
                        .filter((branch) => branch.length > 0)
                        .map((branch) => (branch.length === 1 ? branch[0] : branch));
                    actions = [
                        {
                            parallel: flattenedBranches,
                        },
                    ];
                }
            }
        }
        else {
            actions = [];
            warnings.push('No actions found after trigger nodes');
        }
        const automation = {
            alias: flow.name,
            description: flow.description || '',
            triggers: triggers,
        };
        if (rootConditions && rootConditions.length > 0) {
            automation.conditions = rootConditions;
        }
        automation.actions = actions;
        automation.mode = flow.metadata?.mode ?? 'single';
        // Add optional metadata
        if (flow.metadata?.max) {
            automation.max = flow.metadata.max;
        }
        if (flow.metadata?.max_exceeded) {
            automation.max_exceeded = flow.metadata.max_exceeded;
        }
        if (flow.metadata?.initial_state === false) {
            automation.initial_state = false;
        }
        if (flow.metadata?.trace) {
            automation.trace = flow.metadata.trace;
        }
        if (flow.userTriggerVariables && Object.keys(flow.userTriggerVariables).length > 0) {
            automation.trigger_variables = flow.userTriggerVariables;
        }
        return {
            automation,
            warnings,
            strategy: this.name,
        };
    }
    /**
     * Detect repeat patterns by structurally analyzing back-edges in the graph.
     * Classification rules:
     * - Back-edge target is a condition, source is NOT a condition → while
     * - Back-edge source is a condition with sourceHandle='false' → until
     * - Back-edge source is a condition with sourceHandle='true' → count
     */
    detectRepeatPatterns(flow) {
        const patterns = new Map();
        for (const edge of flow.edges) {
            if (!this.backEdgeIds.has(edge.id))
                continue;
            const sourceNode = this.getNode(flow, edge.source);
            const targetNode = this.getNode(flow, edge.target);
            if (!sourceNode || !targetNode)
                continue;
            if (targetNode.type === 'condition' && sourceNode.type !== 'condition') {
                // ── while pattern ──
                // Back-edge: last body node → first condition node
                const firstCondId = edge.target;
                const backEdgeSourceId = edge.source;
                // Collect condition chain: follow true edges from condition to condition
                const conditionNodeIds = [];
                let currentId = firstCondId;
                while (currentId) {
                    const node = this.getNode(flow, currentId);
                    if (node?.type !== 'condition')
                        break;
                    conditionNodeIds.push(currentId);
                    const trueEdge = flow.edges.find((e) => e.source === currentId && e.sourceHandle === 'true' && !this.backEdgeIds.has(e.id));
                    if (!trueEdge)
                        break;
                    const nextNode = this.getNode(flow, trueEdge.target);
                    if (nextNode?.type === 'condition' && conditionNodeIds.indexOf(trueEdge.target) === -1) {
                        currentId = trueEdge.target;
                    }
                    else {
                        break;
                    }
                }
                // Body nodes: everything between last condition's true target and back-edge source
                const lastCondId = conditionNodeIds[conditionNodeIds.length - 1];
                const bodyNodeIds = this.collectBodyNodes(flow, lastCondId, 'true', new Set(conditionNodeIds), backEdgeSourceId);
                // Exit: first condition's false path
                const falseEdge = flow.edges.find((e) => e.source === firstCondId && e.sourceHandle === 'false' && !this.backEdgeIds.has(e.id));
                patterns.set(firstCondId, {
                    type: 'while',
                    entryNodeId: firstCondId,
                    conditionNodeIds,
                    bodyNodeIds,
                    backEdgeSourceId,
                    exitNodeId: falseEdge?.target ?? null,
                });
            }
            else if (sourceNode.type === 'condition' && edge.sourceHandle === 'false') {
                // ── until pattern ──
                // Back-edge: condition →(false)→ first body node
                const firstBodyId = edge.target;
                const firstCondId = edge.source;
                const conditionNodeIds = [];
                let condId = firstCondId;
                while (condId) {
                    const node = this.getNode(flow, condId);
                    if (node?.type !== 'condition')
                        break;
                    conditionNodeIds.push(condId);
                    const trueEdge = flow.edges.find((e) => e.source === condId && e.sourceHandle === 'true' && !this.backEdgeIds.has(e.id));
                    if (!trueEdge)
                        break;
                    const nextNode = this.getNode(flow, trueEdge.target);
                    if (nextNode?.type === 'condition' && conditionNodeIds.indexOf(trueEdge.target) === -1) {
                        condId = trueEdge.target;
                    }
                    else {
                        break;
                    }
                }
                // Body nodes: traverse forward from firstBodyId until we hit the condition
                const bodyNodeIds = this.collectNodesUntil(flow, firstBodyId, new Set(conditionNodeIds));
                // Exit: last condition's true path
                const lastCondId = conditionNodeIds[conditionNodeIds.length - 1];
                const trueEdge = flow.edges.find((e) => e.source === lastCondId && e.sourceHandle === 'true' && !this.backEdgeIds.has(e.id));
                patterns.set(firstBodyId, {
                    type: 'until',
                    entryNodeId: firstBodyId,
                    conditionNodeIds,
                    bodyNodeIds,
                    backEdgeSourceId: firstCondId,
                    exitNodeId: trueEdge?.target ?? null,
                });
            }
            else if (sourceNode.type === 'condition' && edge.sourceHandle === 'true') {
                // ── count pattern ──
                // Back-edge: condition →(true)→ first body node
                const loopTargetId = edge.target;
                const condId = edge.source;
                const conditionNodeIds = [condId];
                // Find the increment node: it's a set_variables predecessor of the condition
                const condPredEdges = flow.edges.filter((e) => e.target === condId && !this.backEdgeIds.has(e.id));
                const incrementNodeId = condPredEdges.length > 0 ? condPredEdges[0].source : undefined;
                // Body nodes: from loopTargetId to incrementNodeId
                const stopSet = new Set([condId]);
                if (incrementNodeId)
                    stopSet.add(incrementNodeId);
                const bodyNodeIds = this.collectNodesUntil(flow, loopTargetId, stopSet);
                // Find the init node: predecessor of the first body node that is set_variables
                const initCandidates = flow.edges
                    .filter((e) => e.target === loopTargetId && !this.backEdgeIds.has(e.id))
                    .map((e) => e.source);
                const initNodeId = initCandidates.find((id) => {
                    const n = this.getNode(flow, id);
                    return n?.type === 'set_variables';
                });
                // Extract count from the condition's value_template
                const condNode = this.getNode(flow, condId);
                let countValue;
                if (condNode?.type === 'condition' && condNode.data.condition === 'template') {
                    const tmpl = condNode.data.value_template;
                    if (typeof tmpl === 'string') {
                        // Extract N from "{{ _repeat_counter_xxx < N }}"
                        const match = tmpl.match(/<\s*(\d+)\s*\}\}/);
                        if (match) {
                            countValue = Number.parseInt(match[1], 10);
                        }
                    }
                }
                // Exit: condition's false path
                const falseEdge = flow.edges.find((e) => e.source === condId && e.sourceHandle === 'false' && !this.backEdgeIds.has(e.id));
                // Entry is the init node if it exists, otherwise the first body node
                const entryNodeId = initNodeId ?? loopTargetId;
                patterns.set(entryNodeId, {
                    type: 'count',
                    entryNodeId,
                    conditionNodeIds,
                    bodyNodeIds,
                    backEdgeSourceId: condId,
                    initNodeId,
                    incrementNodeId,
                    count: countValue,
                    exitNodeId: falseEdge?.target ?? null,
                });
            }
        }
        return patterns;
    }
    /**
     * Collect body node IDs starting from a condition's specified handle path
     * until reaching the backEdgeSource (inclusive)
     */
    collectBodyNodes(flow, condNodeId, handle, excludeIds, backEdgeSourceId) {
        const startEdge = flow.edges.find((e) => e.source === condNodeId && e.sourceHandle === handle && !this.backEdgeIds.has(e.id));
        if (!startEdge)
            return [];
        const bodyIds = [];
        const queue = [startEdge.target];
        const visited = new Set();
        while (queue.length > 0) {
            const id = queue.shift();
            if (visited.has(id) || excludeIds.has(id))
                continue;
            visited.add(id);
            bodyIds.push(id);
            if (id === backEdgeSourceId)
                continue; // Don't traverse beyond back-edge source
            const outgoing = flow.edges.filter((e) => e.source === id && !this.backEdgeIds.has(e.id));
            for (const e of outgoing) {
                if (!visited.has(e.target) && !excludeIds.has(e.target)) {
                    queue.push(e.target);
                }
            }
        }
        return bodyIds;
    }
    /**
     * Collect node IDs by traversing forward until hitting any node in stopIds
     */
    collectNodesUntil(flow, startId, stopIds) {
        const bodyIds = [];
        const queue = [startId];
        const visited = new Set();
        while (queue.length > 0) {
            const id = queue.shift();
            if (visited.has(id) || stopIds.has(id))
                continue;
            visited.add(id);
            bodyIds.push(id);
            const outgoing = flow.edges.filter((e) => e.source === id && !this.backEdgeIds.has(e.id));
            for (const e of outgoing) {
                if (!visited.has(e.target) && !stopIds.has(e.target)) {
                    queue.push(e.target);
                }
            }
        }
        return bodyIds;
    }
    /**
     * Build a repeat: YAML block from a detected repeat pattern
     */
    buildRepeatBlock(flow, pattern, visited) {
        // Mark all pattern nodes as visited
        for (const id of pattern.conditionNodeIds)
            visited.add(id);
        for (const id of pattern.bodyNodeIds)
            visited.add(id);
        if (pattern.initNodeId)
            visited.add(pattern.initNodeId);
        if (pattern.incrementNodeId)
            visited.add(pattern.incrementNodeId);
        visited.add(pattern.entryNodeId);
        // Build the body sequence
        const bodySequence = [];
        for (const bodyNodeId of pattern.bodyNodeIds) {
            const bodyNode = this.getNode(flow, bodyNodeId);
            if (!bodyNode)
                continue;
            if (bodyNode.type === 'condition') {
                // Check if this condition has branching within the loop body
                const outgoing = flow.edges.filter((e) => e.source === bodyNodeId && !this.backEdgeIds.has(e.id));
                const truePath = outgoing.find((e) => e.sourceHandle === 'true');
                const falsePath = outgoing.find((e) => e.sourceHandle === 'false');
                if (truePath || falsePath) {
                    // Build as if/then/else within the loop body
                    const condAction = {
                        if: [this.buildCondition(bodyNode)],
                        then: [],
                        else: [],
                    };
                    if (truePath && pattern.bodyNodeIds.includes(truePath.target)) {
                        condAction.then = this.buildBodySubsequence(flow, truePath.target, pattern, visited);
                    }
                    if (falsePath && pattern.bodyNodeIds.includes(falsePath.target)) {
                        condAction.else = this.buildBodySubsequence(flow, falsePath.target, pattern, visited);
                    }
                    bodySequence.push(condAction);
                }
                else {
                    // Inline condition guard
                    bodySequence.push(this.buildCondition(bodyNode));
                }
            }
            else {
                const action = this.buildNodeAction(bodyNode);
                if (action) {
                    bodySequence.push(action);
                }
            }
        }
        // Get alias from the first condition (while) or from the init node (count)
        let alias;
        if (pattern.type === 'while') {
            // Build while conditions
            const whileConditions = pattern.conditionNodeIds.map((id) => {
                const node = this.getNode(flow, id);
                if (!alias && node?.data?.alias)
                    alias = node.data.alias;
                return this.buildCondition(node);
            });
            const result = {
                repeat: {
                    while: whileConditions,
                    sequence: bodySequence,
                },
            };
            if (alias)
                result.alias = alias;
            return result;
        }
        if (pattern.type === 'until') {
            const untilConditions = pattern.conditionNodeIds.map((id) => {
                const node = this.getNode(flow, id);
                return this.buildCondition(node);
            });
            const result = {
                repeat: {
                    until: untilConditions,
                    sequence: bodySequence,
                },
            };
            if (alias)
                result.alias = alias;
            return result;
        }
        if (pattern.type === 'count') {
            if (pattern.initNodeId) {
                const initNode = this.getNode(flow, pattern.initNodeId);
                if (initNode?.data && 'alias' in initNode.data) {
                    alias = initNode.data.alias;
                }
            }
            const result = {
                repeat: {
                    count: pattern.count,
                    sequence: bodySequence,
                },
            };
            if (alias)
                result.alias = alias;
            return result;
        }
        return null;
    }
    /**
     * Build a sub-sequence within a repeat body for branching paths
     */
    buildBodySubsequence(flow, startId, pattern, visited) {
        const sequence = [];
        let currentId = startId;
        while (currentId && pattern.bodyNodeIds.includes(currentId) && !visited.has(currentId)) {
            visited.add(currentId);
            const node = this.getNode(flow, currentId);
            if (!node)
                break;
            const action = this.buildNodeAction(node);
            if (action) {
                sequence.push(action);
            }
            const outgoing = flow.edges.filter((e) => e.source === currentId && !this.backEdgeIds.has(e.id));
            currentId = outgoing.length === 1 ? outgoing[0].target : null;
        }
        return sequence;
    }
    /**
     * Extract trigger configurations from trigger nodes
     */
    extractTriggers(flow) {
        return flow.nodes
            .filter((n) => n.type === 'trigger')
            .map((node) => this.buildTrigger(node));
    }
    /**
     * Detect if multiple first actions form an OR pattern
     * (all are conditions whose true OR false paths converge to the same node)
     */
    detectOrPattern(flow, firstActionIds) {
        // All first actions must be condition nodes
        const conditions = firstActionIds.map((id) => this.getNode(flow, id));
        if (!conditions.every((n) => n?.type === 'condition')) {
            return null;
        }
        // Check if all true paths converge to the same node
        const trueTargets = new Set();
        for (const cond of conditions) {
            const trueEdge = flow.edges.find((e) => e.source === cond.id && e.sourceHandle === 'true');
            if (trueEdge) {
                trueTargets.add(trueEdge.target);
            }
        }
        if (trueTargets.size === 1 && conditions.length === firstActionIds.length) {
            // All conditions have the same true target
            const convergenceNode = [...trueTargets][0];
            return {
                conditions: conditions,
                convergenceNode,
                isFromFalsePaths: false,
            };
        }
        // Check if all false paths converge to the same node
        const falseTargets = new Set();
        for (const cond of conditions) {
            const falseEdge = flow.edges.find((e) => e.source === cond.id && e.sourceHandle === 'false');
            if (falseEdge) {
                falseTargets.add(falseEdge.target);
            }
        }
        if (falseTargets.size === 1 && conditions.length === firstActionIds.length) {
            // All conditions have the same false target
            const convergenceNode = [...falseTargets][0];
            return {
                conditions: conditions,
                convergenceNode,
                isFromFalsePaths: true,
            };
        }
        return null;
    }
    /**
     * Extract leading condition nodes that can be promoted to the root conditions block.
     * Only conditions with no false/else path are promotable, forming a straight chain
     * from triggers to actions via true paths only (or false paths only for inverted conditions).
     * Fan-out is allowed when only one handle type is used - the condition is promoted and
     * all fan-out targets become action starting points.
     */
    extractLeadingConditions(flow, startNodeId) {
        const conditions = [];
        const visitedIds = new Set();
        let currentId = startNodeId;
        while (currentId) {
            const node = this.getNode(flow, currentId);
            if (!node || node.type !== 'condition')
                break;
            // Don't promote conditions that are part of a repeat pattern
            if (this.repeatPatterns.has(currentId) || this.repeatInternalNodeIds.has(currentId))
                break;
            // Don't promote a condition that came from inside the action sequence. The
            // root conditions block is not equivalent: automation.trigger skips it by
            // default, so an automation another one calls would stop checking (#248).
            if (isConditionInSequence(node.data))
                break;
            const allOutgoing = this.getOutgoingEdges(flow, currentId);
            const outgoing = allOutgoing.filter((e) => !this.backEdgeIds.has(e.id));
            const truePaths = outgoing.filter((edge) => edge.sourceHandle === 'true');
            const falsePaths = outgoing.filter((edge) => edge.sourceHandle === 'false');
            // Can only promote if the condition uses only ONE handle type (no branching to different outcomes)
            // If both handles are used, it's a full if/then/else and cannot be promoted
            if (truePaths.length > 0 && falsePaths.length > 0)
                break;
            // Must have at least one path
            if (truePaths.length === 0 && falsePaths.length === 0)
                break;
            // Build the condition object with alias preserved
            const condition = this.buildCondition(node);
            if (node.data.alias) {
                condition.alias = node.data.alias;
            }
            if (falsePaths.length > 0) {
                // Connected via false handle only → inverted condition, wrap in "not"
                conditions.push({
                    condition: 'not',
                    conditions: [condition],
                });
                visitedIds.add(currentId);
                // If there's fan-out (multiple false paths), stop extraction and return all targets
                if (falsePaths.length > 1) {
                    return { conditions, nextNodeIds: falsePaths.map((e) => e.target), visitedIds };
                }
                currentId = falsePaths[0].target;
            }
            else {
                // Connected via true handle only → promote as-is
                conditions.push(condition);
                visitedIds.add(currentId);
                // If there's fan-out (multiple true paths), stop extraction and return all targets
                if (truePaths.length > 1) {
                    return { conditions, nextNodeIds: truePaths.map((e) => e.target), visitedIds };
                }
                currentId = truePaths[0].target;
            }
        }
        return { conditions, nextNodeIds: currentId ? [currentId] : [], visitedIds };
    }
    /**
     * Build a single trigger configuration
     */
    /**
     * Find condition nodes whose specified handle (true/false) points to a given target node
     * Returns the condition sources if there are multiple (OR pattern), empty array otherwise
     */
    findOrConditionSources(flow, targetNodeId, handleType, visited) {
        const sources = flow.edges
            .filter((e) => e.target === targetNodeId && e.sourceHandle === handleType && !this.backEdgeIds.has(e.id))
            .map((e) => this.getNode(flow, e.source))
            .filter((n) => n?.type === 'condition' && !visited.has(n.id));
        return sources.length > 1 ? sources : [];
    }
    /**
     * Recursively build action sequence from a node
     */
    buildSequenceFromNode(flow, nodeId, visited) {
        if (visited.has(nodeId)) {
            return []; // Avoid infinite loops
        }
        const node = this.getNode(flow, nodeId);
        if (!node) {
            return [];
        }
        const sequence = [];
        // Check if this node is an OR convergence point (multiple conditions' true/false paths converge here)
        const orTrueSources = this.findOrConditionSources(flow, nodeId, 'true', visited);
        const orFalseSources = this.findOrConditionSources(flow, nodeId, 'false', visited);
        if (orTrueSources.length > 1) {
            // Multiple conditions' TRUE paths converge here - build OR block
            const orConditions = orTrueSources.map((c) => this.buildCondition(c));
            // Mark these conditions as visited
            for (const c of orTrueSources) {
                visited.add(c.id);
            }
            // Now add the current node to visited and build the then sequence
            visited.add(nodeId);
            const thenSequence = node.type === 'condition'
                ? this.buildSequenceFromNode(flow, nodeId, new Set()) // Process this condition node fresh
                : this.buildSequenceFromNode(flow, nodeId, new Set(visited));
            // For OR conditions, prepend the current node's action to the then sequence if it's not a condition
            let finalThenSequence;
            if (node.type !== 'condition') {
                const currentAction = this.buildNodeAction(node);
                finalThenSequence = currentAction ? [currentAction, ...thenSequence] : thenSequence;
            }
            else {
                finalThenSequence = thenSequence;
            }
            sequence.push({
                if: [{ condition: 'or', conditions: orConditions }],
                then: finalThenSequence,
                else: [], // OR conditions don't have a shared else path
            });
            return sequence;
        }
        if (orFalseSources.length > 1) {
            // Multiple conditions' FALSE paths converge here - build OR block (negated logic)
            // When false paths converge, it means "if NOT cond1 AND NOT cond2" which is equivalent to "if NOT (cond1 OR cond2)"
            const orConditions = orFalseSources.map((c) => this.buildCondition(c));
            // Mark these conditions as visited
            for (const c of orFalseSources) {
                visited.add(c.id);
            }
            // Now add the current node to visited and build the then sequence
            visited.add(nodeId);
            const thenSequence = node.type === 'condition'
                ? this.buildSequenceFromNode(flow, nodeId, new Set())
                : this.buildSequenceFromNode(flow, nodeId, new Set(visited));
            // For OR conditions, prepend the current node's action to the then sequence if it's not a condition
            let finalThenSequence;
            if (node.type !== 'condition') {
                const currentAction = this.buildNodeAction(node);
                finalThenSequence = currentAction ? [currentAction, ...thenSequence] : thenSequence;
            }
            else {
                finalThenSequence = thenSequence;
            }
            // Since false paths converge, we negate by swapping then/else
            // "if any condition is false, do this" = "if NOT(all conditions true), do this"
            sequence.push({
                if: [{ condition: 'or', conditions: orConditions }],
                then: [], // When OR is true, we don't execute (this is the "else" in normal terms)
                else: finalThenSequence, // When OR is false (all conditions false), execute
            });
            return sequence;
        }
        // Check if this node is the entry point of a repeat pattern
        const repeatPattern = this.repeatPatterns.get(nodeId);
        if (repeatPattern) {
            const repeatBlock = this.buildRepeatBlock(flow, repeatPattern, visited);
            if (repeatBlock) {
                sequence.push(repeatBlock);
                // Continue from the exit node
                if (repeatPattern.exitNodeId) {
                    const afterRepeat = this.buildSequenceFromNode(flow, repeatPattern.exitNodeId, new Set(visited));
                    sequence.push(...afterRepeat);
                }
                return sequence;
            }
        }
        // Normal processing - add to visited now
        visited.add(nodeId);
        // Get outgoing edges (excluding repeat back-edges)
        const outgoing = this.getOutgoingEdges(flow, nodeId).filter((e) => !this.backEdgeIds.has(e.id));
        if (node.type === 'condition') {
            // ===== Condition Chain Logic =====
            // This logic identifies chains of conditions and merges them into a single 'choose'
            const conditions = [];
            let currentNode = node;
            let thenNodeIds = [];
            let elseNodeIds = [];
            // The 'else' paths are taken from the very first condition in the chain
            // Use filter to get ALL false edges, not just the first (handles fan-out patterns)
            const originalElsePaths = this.getOutgoingEdges(flow, node.id).filter((edge) => edge.sourceHandle === 'false' && !this.backEdgeIds.has(edge.id));
            elseNodeIds = originalElsePaths.map((edge) => edge.target);
            // Start traversing the 'true' path to find all sequential conditions
            // Only chain conditions that share the same "else" behavior (no else, or same else target)
            while (currentNode?.type === 'condition') {
                conditions.push(this.buildCondition(currentNode));
                // Get ALL true paths (handles fan-out patterns where multiple conditions branch from same handle)
                const truePaths = this.getOutgoingEdges(flow, currentNode.id).filter((edge) => edge.sourceHandle === 'true' && !this.backEdgeIds.has(edge.id));
                if (truePaths.length === 0) {
                    // No true paths - end of chain
                    break;
                }
                // If there are multiple true paths (fan-out), we can't chain conditions
                // Each branch becomes a separate action in the then sequence
                if (truePaths.length > 1) {
                    thenNodeIds = truePaths.map((edge) => edge.target);
                    break;
                }
                // Single true path - check if we can chain to another condition
                const truePath = truePaths[0];
                const nextNode = this.getNode(flow, truePath.target);
                // If the next node is a condition and not visited, check if we should continue chaining
                // Never chain conditions that are part of a repeat pattern
                if (nextNode?.type === 'condition' &&
                    !visited.has(nextNode.id) &&
                    !this.repeatPatterns.has(nextNode.id) &&
                    !this.repeatInternalNodeIds.has(nextNode.id)) {
                    // Check if the next condition has false paths
                    const nextFalsePaths = this.getOutgoingEdges(flow, nextNode.id).filter((edge) => edge.sourceHandle === 'false' && !this.backEdgeIds.has(edge.id));
                    // Only continue chaining if:
                    // 1. The next condition has no false path (it can be merged), OR
                    // 2. The next condition has exactly one false path going to one of the original else targets
                    const canChain = nextFalsePaths.length === 0 ||
                        (nextFalsePaths.length === 1 && elseNodeIds.includes(nextFalsePaths[0].target));
                    if (canChain) {
                        currentNode = nextNode;
                        visited.add(currentNode.id); // Mark as visited to avoid re-processing
                    }
                    else {
                        // The next condition has its own else branch - don't merge it
                        // Instead, it becomes part of the "then" sequence as a nested if
                        thenNodeIds = [truePath.target];
                        break;
                    }
                }
                else {
                    // End of chain: the target is not a condition or is already visited
                    thenNodeIds = [truePath.target];
                    break;
                }
            }
            // Put conditions directly in the if: array - HA implicitly ANDs them
            const chooseAction = {
                alias: node.data.alias, // Use alias from the first condition
                if: conditions,
                then: [],
                else: [],
            };
            // Find convergence point between then and else branches.
            // When both branches lead to the same continuation node, that node should
            // appear AFTER the if/then/else block, not duplicated inside each branch.
            const allBranchStarts = [...thenNodeIds, ...elseNodeIds];
            const convergencePoint = thenNodeIds.length > 0 && elseNodeIds.length > 0
                ? this.findConvergencePoint(flow, allBranchStarts)
                : null;
            if (convergencePoint) {
                // Build each branch only up to the convergence point
                if (thenNodeIds.length > 0) {
                    const thenActions = thenNodeIds.flatMap((id) => this.buildSequenceUntilNode(flow, id, convergencePoint, new Set(visited)));
                    chooseAction.then = thenActions;
                }
                if (elseNodeIds.length > 0) {
                    const elseActions = elseNodeIds.flatMap((id) => this.buildSequenceUntilNode(flow, id, convergencePoint, new Set(visited)));
                    chooseAction.else = elseActions;
                }
                sequence.push(chooseAction);
                // Continue from the convergence point after the condition block
                const afterCondition = this.buildSequenceFromNode(flow, convergencePoint, new Set(visited));
                sequence.push(...afterCondition);
            }
            else {
                // No convergence — build each branch independently
                if (thenNodeIds.length > 0) {
                    const thenActions = thenNodeIds.flatMap((id) => this.buildSequenceFromNode(flow, id, new Set(visited)));
                    chooseAction.then = thenActions;
                }
                if (elseNodeIds.length > 0) {
                    const elseActions = elseNodeIds.flatMap((id) => this.buildSequenceFromNode(flow, id, new Set(visited)));
                    chooseAction.else = elseActions;
                }
                sequence.push(chooseAction);
            }
        }
        else {
            // ===== Default Logic for Non-Condition Nodes =====
            const action = this.buildNodeAction(node);
            if (action) {
                sequence.push(action);
            }
            if (outgoing.length === 1) {
                // Single outgoing edge - continue the sequence
                const nextActions = this.buildSequenceFromNode(flow, outgoing[0].target, new Set(visited));
                sequence.push(...nextActions);
            }
            else if (outgoing.length > 1) {
                // Multiple outgoing edges (parallel paths)
                const convergencePoint = this.findConvergencePoint(flow, outgoing.map((e) => e.target));
                if (convergencePoint) {
                    const parallelActions = outgoing.map((edge) => this.buildSequenceUntilNode(flow, edge.target, convergencePoint, new Set(visited)));
                    const filteredBranches = parallelActions.filter((a) => a.length > 0);
                    if (filteredBranches.length > 0) {
                        // Flatten single-action branches to avoid double-nesting (- - service:)
                        const flattenedBranches = filteredBranches.map((branch) => branch.length === 1 ? branch[0] : branch);
                        sequence.push({
                            parallel: flattenedBranches,
                        });
                    }
                    const afterParallel = this.buildSequenceFromNode(flow, convergencePoint, new Set(visited));
                    sequence.push(...afterParallel);
                }
                else {
                    const parallelActions = outgoing.map((edge) => this.buildSequenceFromNode(flow, edge.target, new Set(visited)));
                    const filteredBranches = parallelActions.filter((a) => a.length > 0);
                    if (filteredBranches.length > 0) {
                        // Flatten single-action branches to avoid double-nesting (- - service:)
                        const flattenedBranches = filteredBranches.map((branch) => branch.length === 1 ? branch[0] : branch);
                        sequence.push({
                            parallel: flattenedBranches,
                        });
                    }
                }
            }
        }
        return sequence;
    }
    /**
     * Find the convergence point where multiple branches meet
     * Returns the node ID if all branches converge, null otherwise
     */
    findConvergencePoint(flow, branchStarts) {
        if (branchStarts.length < 2)
            return null;
        // For each branch, find all reachable nodes
        const reachableSets = branchStarts.map((startId) => {
            const reachable = new Set();
            const queue = [startId];
            while (queue.length > 0) {
                const nodeId = queue.shift();
                if (reachable.has(nodeId))
                    continue;
                reachable.add(nodeId);
                const outgoing = this.getOutgoingEdges(flow, nodeId);
                for (const edge of outgoing) {
                    queue.push(edge.target);
                }
            }
            return reachable;
        });
        // Find nodes that are reachable from ALL branches
        const firstSet = reachableSets[0];
        const commonNodes = [...firstSet].filter((nodeId) => reachableSets.every((set) => set.has(nodeId)));
        if (commonNodes.length === 0)
            return null;
        // Find the earliest common node (closest to the branch starts)
        // by checking which node has the minimum maximum distance from any branch start
        let bestNode = null;
        let bestMaxDistance = Number.POSITIVE_INFINITY;
        for (const nodeId of commonNodes) {
            const distances = branchStarts.map((startId) => this.getShortestDistance(flow, startId, nodeId));
            const maxDist = Math.max(...distances);
            if (maxDist < bestMaxDistance) {
                bestMaxDistance = maxDist;
                bestNode = nodeId;
            }
        }
        return bestNode;
    }
    /**
     * Get shortest distance from start to target node using BFS
     */
    getShortestDistance(flow, startId, targetId) {
        if (startId === targetId)
            return 0;
        const visited = new Set();
        const queue = [{ nodeId: startId, distance: 0 }];
        while (queue.length > 0) {
            const { nodeId, distance } = queue.shift();
            if (visited.has(nodeId))
                continue;
            visited.add(nodeId);
            const outgoing = this.getOutgoingEdges(flow, nodeId);
            for (const edge of outgoing) {
                if (edge.target === targetId) {
                    return distance + 1;
                }
                if (!visited.has(edge.target)) {
                    queue.push({ nodeId: edge.target, distance: distance + 1 });
                }
            }
        }
        return Number.POSITIVE_INFINITY;
    }
    /**
     * Build sequence from a node until reaching the stop node (exclusive)
     */
    buildSequenceUntilNode(flow, nodeId, stopNodeId, visited) {
        if (nodeId === stopNodeId) {
            return []; // Don't include the stop node
        }
        if (visited.has(nodeId)) {
            return []; // Avoid infinite loops
        }
        visited.add(nodeId);
        const node = this.getNode(flow, nodeId);
        if (!node) {
            return [];
        }
        const sequence = [];
        // Build the current node's action
        const action = this.buildNodeAction(node);
        if (action) {
            sequence.push(action);
        }
        // Get outgoing edges (excluding repeat back-edges)
        const outgoing = this.getOutgoingEdges(flow, nodeId).filter((e) => !this.backEdgeIds.has(e.id));
        if (node.type === 'condition') {
            // Condition nodes are handled specially
            const chooseAction = action;
            const truePath = outgoing.filter((edge) => edge.sourceHandle === 'true');
            const falsePath = outgoing.filter((edge) => edge.sourceHandle === 'false');
            if (truePath.length > 0) {
                const thenActions = truePath.flatMap((edge) => this.buildSequenceUntilNode(flow, edge.target, stopNodeId, new Set(visited)));
                chooseAction.then = thenActions;
            }
            if (falsePath.length > 0) {
                const elseActions = falsePath.flatMap((edge) => this.buildSequenceUntilNode(flow, edge.target, stopNodeId, new Set(visited)));
                chooseAction.else = elseActions;
            }
        }
        else if (outgoing.length === 1) {
            // Single outgoing edge - continue if not at stop node
            if (outgoing[0].target !== stopNodeId) {
                const nextActions = this.buildSequenceUntilNode(flow, outgoing[0].target, stopNodeId, new Set(visited));
                sequence.push(...nextActions);
            }
        }
        else if (outgoing.length > 1) {
            // Multiple outgoing edges - this is a nested parallel inside a parallel
            // For now, just build all branches until stop node
            const parallelActions = outgoing.map((edge) => this.buildSequenceUntilNode(flow, edge.target, stopNodeId, new Set(visited)));
            const filteredBranches = parallelActions.filter((a) => a.length > 0);
            if (filteredBranches.length > 0) {
                // Flatten single-action branches to avoid double-nesting (- - service:)
                const flattenedBranches = filteredBranches.map((branch) => branch.length === 1 ? branch[0] : branch);
                sequence.push({
                    parallel: flattenedBranches,
                });
            }
        }
        return sequence;
    }
    /**
     * Build action configuration for a single node
     */
    buildNodeAction(node) {
        switch (node.type) {
            case 'trigger':
                return null; // Triggers are handled separately
            case 'condition':
                return this.buildConditionChoose(node);
            case 'action':
                return this.buildActionCall(node);
            case 'delay':
                return this.buildDelay(node);
            case 'wait':
                return this.buildWait(node);
            case 'set_variables':
                return this.buildSetVariables(node);
            default:
                return null;
        }
    }
    /**
     * Build a choose block for a condition node
     */
    buildConditionChoose(node) {
        // Build the full condition including any nested conditions
        const condition = this.buildCondition(node);
        const choose = {
            alias: node.data.alias,
            if: [condition],
            then: [], // Will be filled by the caller
            else: [], // Will be filled by the caller
        };
        // Note: 'id' for trigger conditions belongs inside the condition object, not at the if/then/else level
        // The id is already included via buildCondition's ...rest spread
        return choose;
    }
    /**
     * Map a single condition object (used for individual conditions in an array)
     */
    mapSingleCondition(data) {
        const { condition, conditions, alias, template, ...rest } = stripCafeNodeData(data);
        const out = {
            condition: condition,
            ...rest,
        };
        // For template conditions, ensure value_template is set from template if needed
        if (condition === 'template' && !rest.value_template && template) {
            out.value_template = template;
        }
        // Recursively map nested group conditions
        if (Array.isArray(conditions) && conditions.length > 0) {
            out.conditions = conditions
                .map((c) => this.mapSingleCondition(c))
                .filter((c) => c && (!Array.isArray(c.conditions) || c.conditions.length > 0));
        }
        return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined && v !== ''));
    }
    /**
     * Build condition configuration
     */
    buildCondition(node) {
        // Helper to recursively map condition to condition
        function mapCondition(data) {
            if (!data || typeof data !== 'object')
                return data;
            // Destructure and exclude 'template' - HA uses 'value_template' for template conditions
            const { condition, conditions, alias, template, ...rest } = stripCafeNodeData(data);
            const out = {
                condition: condition,
                ...rest,
            };
            // For template conditions, ensure value_template is set from template if needed
            if (condition === 'template' && !rest.value_template && template) {
                out.value_template = template;
            }
            // Recursively map nested group conditions
            if (Array.isArray(conditions) && conditions.length > 0) {
                out.conditions = conditions
                    .map(mapCondition)
                    .filter((c) => c && (!Array.isArray(c.conditions) || c.conditions.length > 0));
            }
            return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined && v !== ''));
        }
        return mapCondition(node.data);
    }
    /**
     * Build service call action or device action
     */
    buildActionCall(node) {
        // Check if this is a device action (needs special format)
        if (isDeviceAction(node.data.data)) {
            const deviceData = node.data.data;
            const action = {
                device_id: deviceData.device_id,
                domain: deviceData.domain,
                type: deviceData.type,
            };
            if (node.data.alias) {
                action.alias = node.data.alias;
            }
            // Add entity_id if present
            if (deviceData.entity_id) {
                action.entity_id = deviceData.entity_id;
            }
            // Add subtype if present
            if (deviceData.subtype) {
                action.subtype = deviceData.subtype;
            }
            // Add any additional parameters (like 'option' for select)
            const knownFields = ['type', 'device_id', 'domain', 'entity_id', 'subtype'];
            for (const [key, value] of Object.entries(deviceData)) {
                if (!knownFields.includes(key) && value !== undefined) {
                    action[key] = value;
                }
            }
            if (node.data.enabled === false) {
                action.enabled = false;
            }
            return action;
        }
        // Check if this is a fallback repeat action (opaque repeat block)
        if (node.data.repeat) {
            const repeatData = node.data.repeat;
            const action = {
                repeat: {
                    ...(repeatData.count !== undefined ? { count: repeatData.count } : {}),
                    ...(repeatData.while ? { while: repeatData.while } : {}),
                    ...(repeatData.until ? { until: repeatData.until } : {}),
                    sequence: repeatData.sequence ?? [],
                },
            };
            if (node.data.alias)
                action.alias = node.data.alias;
            if (node.data.enabled === false)
                action.enabled = false;
            return action;
        }
        // Check if this is a fire event action
        if (typeof node.data.event === 'string' && node.data.event.trim() !== '') {
            const action = { event: node.data.event };
            if (node.data.alias)
                action.alias = node.data.alias;
            if (node.data.event_data && Object.keys(node.data.event_data).length > 0) {
                action.event_data = node.data.event_data;
            }
            if (node.data.enabled === false)
                action.enabled = false;
            return action;
        }
        // Check if this is a stop action
        if ('stop' in node.data) {
            const action = { stop: node.data.stop ?? '' };
            if (node.data.alias)
                action.alias = node.data.alias;
            if (node.data.error === true)
                action.error = true;
            if (node.data.enabled === false)
                action.enabled = false;
            return action;
        }
        // Standard service call format
        // Use spread pattern to preserve unknown properties from custom integrations
        const { alias, service, 
        // `id` is intentionally dropped (not just excluded from extraProps) —
        // HA's action-step schemas (service call, delay, wait, set_variables)
        // don't support a per-step `id:` at all; only triggers do. Real HA
        // rejects it outright ("extra keys not allowed"), so it can't be
        // preserved even for round-trip fidelity.
        id: _id, target, data, data_template, response_variable, continue_on_error, enabled, repeat: _repeat, ...extraProps } = node.data;
        const action = {
            ...extraProps, // Preserve extra properties
            alias,
            service,
        };
        if (target) {
            action.target = target;
        }
        if (data) {
            action.data = data;
        }
        if (data_template) {
            action.data_template = data_template;
        }
        if (response_variable) {
            action.response_variable = response_variable;
        }
        if (continue_on_error) {
            action.continue_on_error = continue_on_error;
        }
        if (enabled === false) {
            action.enabled = false;
        }
        return action;
    }
    /**
     * Build delay action
     */
    buildDelay(node) {
        // Use spread pattern to preserve unknown properties from custom integrations.
        // `id` is dropped — HA's action-step schemas don't support it, only triggers do.
        const { alias, delay: delayValue, id: _id, ...extraProps } = node.data;
        const delay = {
            ...extraProps, // Preserve extra properties
            alias,
            delay: delayValue,
        };
        return delay;
    }
    /**
     * Build wait action
     */
    buildWait(node) {
        // Use spread pattern to preserve unknown properties from custom integrations.
        // `id` is dropped — HA's action-step schemas don't support it, only triggers do.
        const { alias, id: _id, wait_template, wait_for_trigger, timeout, continue_on_timeout, ...extraProps } = node.data;
        const wait = {
            ...extraProps, // Preserve extra properties
            alias,
        };
        if (wait_template) {
            wait.wait_template = wait_template;
        }
        else if (wait_for_trigger) {
            wait.wait_for_trigger = wait_for_trigger.map((triggerData) => {
                const trigger = { ...triggerData };
                return Object.fromEntries(Object.entries(trigger).filter(([, v]) => v !== undefined && v !== '' && v !== null));
            });
        }
        if (timeout) {
            wait.timeout = timeout;
        }
        if (continue_on_timeout !== undefined) {
            wait.continue_on_timeout = continue_on_timeout;
        }
        return wait;
    }
    /**
     * Build set variables action
     */
    buildSetVariables(node) {
        // Use spread pattern to preserve unknown properties from custom integrations.
        // `id` is dropped — HA's action-step schemas don't support it, only triggers do.
        const { alias, id: _id, variables, ...extraProps } = node.data;
        const setVars = {
            ...extraProps, // Preserve extra properties
            variables,
        };
        if (alias) {
            setVars.alias = alias;
        }
        return setVars;
    }
}
//# sourceMappingURL=native.js.map